import * as React from 'react';
import type { AnswerValue } from '@/lib/api/secure-links';
import { cn } from '@/lib/utils/cn';
import { plural } from '@/components/discovery/discovery-logic';

/**
 * The questionnaire as its respondent sees it (prototype
 * `clientQuestionnaire()` and `cqInput()` in
 * `docs/discovery/prototype/discovery.js`). One component for the live
 * page and the consultant's preview, so the preview shows exactly what the
 * respondent sees (E12-F08 §1). Without `onAnswer` the controls are inert.
 */

export interface FormQuestion {
  /** The run question id on the live page; the ordinal in a preview. */
  key: string;
  ordinal: number;
  prompt: string;
  answer_type: string;
  options: readonly string[];
}

export function QuestionnaireForm({
  meta,
  title,
  minutes,
  until,
  questions,
  values,
  answered,
  onAnswer,
  footer,
}: {
  /** "City of Marlow · for the Parks department". */
  meta: string;
  title: string;
  minutes: number | null;
  /** "until it expires", or "until 19 Oct 2026". */
  until: string;
  questions: readonly FormQuestion[];
  values?: Readonly<Record<string, AnswerValue>>;
  /** The progress bar shows on the live page only. */
  answered?: number;
  onAnswer?: (key: string, value: AnswerValue | undefined) => void;
  footer: React.ReactNode;
}) {
  return (
    <>
      <div className="text-meta text-ink-500">{meta}</div>
      <h1 className="my-1 text-h1 font-semibold text-ink-900">{title}</h1>
      <p className="text-body text-ink-700">
        {plural(questions.length, 'question')}
        {minutes ? `, about ${minutes} minutes` : ''}. Your answers save as you go, and you can come
        back to this link {until}.
      </p>
      {answered !== undefined && questions.length > 0 ? (
        <div className="mb-2 mt-4">
          <div
            role="progressbar"
            aria-label="Questions answered"
            aria-valuemin={0}
            aria-valuemax={questions.length}
            aria-valuenow={answered}
            className="mb-1 h-1.5 overflow-hidden rounded-full bg-ink-100"
          >
            <i
              className="block h-full max-w-full rounded-full bg-accent-amber-600"
              style={{ width: `${(answered / questions.length) * 100}%` }}
            />
          </div>
          <div className="text-meta text-ink-500">
            {answered} of {questions.length} answered
          </div>
        </div>
      ) : null}
      {questions.map((q, i) => (
        <section
          key={q.key}
          aria-labelledby={`cq-${q.key}`}
          className="border-t border-ink-100 py-[18px]"
        >
          <div className="text-meta text-ink-500">Question {i + 1}</div>
          <h3 id={`cq-${q.key}`} className="mb-2.5 mt-0.5 text-h3 font-semibold text-ink-900">
            {q.prompt}
          </h3>
          <AnswerControl
            question={q}
            value={values?.[q.key]}
            onChange={onAnswer ? (v) => onAnswer(q.key, v) : undefined}
          />
        </section>
      ))}
      {footer}
    </>
  );
}

const FIELD =
  'block w-full rounded-card border border-ink-200 bg-paper-0 px-3 text-body text-ink-900 placeholder:text-ink-300 hover:border-ink-300 focus:outline focus:outline-2 focus:outline-offset-2 focus:outline-accent-amber-600';

const SEG_CELL =
  'inline-flex h-10 min-w-14 items-center justify-center border-r border-ink-200 text-body font-medium last:border-r-0';

const OPTION_CHIP = 'inline-flex h-7 items-center rounded-full border px-2.5 text-meta font-medium';

/** One answer control (`cqInput()`): rating 1–5, options, a number, or text. */
function AnswerControl({
  question,
  value,
  onChange,
}: {
  question: FormQuestion;
  value: AnswerValue | undefined;
  onChange: ((value: AnswerValue | undefined) => void) | undefined;
}) {
  const { answer_type: type, options, prompt } = question;
  const live = onChange !== undefined;

  if (type === 'rating') {
    const rating = value && 'rating' in value ? value.rating : null;
    return (
      <div>
        <div
          role={live ? 'radiogroup' : undefined}
          aria-label={live ? prompt : undefined}
          className="inline-flex overflow-hidden rounded-card border border-ink-200"
        >
          {[1, 2, 3, 4, 5].map((n) =>
            live ? (
              <button
                key={n}
                type="button"
                role="radio"
                aria-checked={rating === n}
                onClick={() => onChange({ rating: n })}
                className={cn(
                  SEG_CELL,
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-accent-amber-600',
                  rating === n
                    ? 'bg-ink-900 text-paper-0'
                    : 'bg-paper-0 text-ink-700 hover:bg-paper-50',
                )}
              >
                {n}
              </button>
            ) : (
              <span key={n} className={cn(SEG_CELL, 'bg-paper-0 text-ink-700')}>
                {n}
              </span>
            ),
          )}
        </div>
        <div className="mt-1 flex max-w-[300px] justify-between text-meta text-ink-500">
          <span>Low</span>
          <span>High</span>
        </div>
      </div>
    );
  }

  if (type === 'single' || type === 'multiple') {
    const picked =
      value && 'options' in value
        ? value.options
        : value && 'option' in value
          ? [value.option]
          : [];
    const toggle = (o: string) => {
      if (!onChange) return;
      if (type === 'single') {
        onChange(picked.includes(o) ? undefined : { option: o });
        return;
      }
      const next = picked.includes(o) ? picked.filter((p) => p !== o) : [...picked, o];
      // Keep the question's own order, whatever order they were picked in.
      onChange(next.length ? { options: options.filter((x) => next.includes(x)) } : undefined);
    };
    return (
      <div>
        <div
          className="flex flex-wrap gap-1.5"
          role={live ? 'group' : undefined}
          aria-label={live ? prompt : undefined}
        >
          {options.map((o) =>
            live ? (
              <button
                key={o}
                type="button"
                aria-pressed={picked.includes(o)}
                onClick={() => toggle(o)}
                className={cn(
                  OPTION_CHIP,
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-amber-600',
                  picked.includes(o)
                    ? 'border-ink-900 bg-ink-900 text-paper-0'
                    : 'border-ink-200 bg-paper-0 text-ink-700 hover:border-ink-300',
                )}
              >
                {o}
              </button>
            ) : (
              <span key={o} className={cn(OPTION_CHIP, 'border-ink-200 bg-paper-0 text-ink-700')}>
                {o}
              </span>
            ),
          )}
        </div>
        <div className="mt-1 text-meta text-ink-500">
          {type === 'single' ? 'Pick one' : 'Pick all that apply'}
        </div>
      </div>
    );
  }

  const text = value && 'text' in value ? value.text : '';
  const change = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
    onChange?.(e.target.value ? { text: e.target.value } : undefined);
  if (type === 'number') {
    return (
      <input
        aria-label={live ? prompt : 'A number'}
        placeholder="A number"
        maxLength={4000}
        className={cn(FIELD, 'h-9 max-w-[200px]')}
        {...(live ? { value: text, onChange: change } : {})}
      />
    );
  }
  return (
    <textarea
      aria-label={live ? prompt : 'Your answer'}
      rows={3}
      placeholder="Type your answer"
      maxLength={4000}
      className={cn(FIELD, 'resize-y py-2 leading-5')}
      {...(live ? { value: text, onChange: change } : {})}
    />
  );
}
